import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuthApi';
import { AlertCircle, CheckCircle2, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AuthLayout from '@/components/auth/AuthLayout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { confirmPasswordReset } = useAuth();

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);

  useEffect(() => {
    // Parse URL fragments for Supabase auth tokens
    const hashParams = new URLSearchParams(location.hash.slice(1));
    const access_token = hashParams.get('access_token');
    const refresh_token = hashParams.get('refresh_token');
    const error = hashParams.get('error');
    const error_description = hashParams.get('error_description');

    if (error) {
      setError(error_description || 'Invalid or expired reset link. Please request a new password reset.');
      return;
    }

    if (access_token && refresh_token) {
      setAccessToken(access_token);
      setRefreshToken(refresh_token);
    } else {
      setError('Invalid or missing reset tokens. Please request a new password reset.');
    }
  }, [location.hash]);

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/(?=.*[a-z])/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/(?=.*[A-Z])/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/(?=.*\d)/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!accessToken || !refreshToken) {
      setError('Invalid or missing reset tokens. Please request a new password reset.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setError(passwordErrors[0]);
      return;
    }

    setIsLoading(true);

    try {
      // For Supabase, we need to pass the access_token as the "token" parameter
      // The backend should use this access_token to update the user's password
      await confirmPasswordReset(accessToken, password);
      setSuccess(true);
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error: any) {
      setError(
        error.message || 'An error occurred while resetting your password. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const authInputClass = 'h-10 rounded-[10px] border-white/10 bg-transparent text-white text-base tracking-[-0.16px] placeholder:text-[#b2b2b2] focus-visible:ring-white/30 focus-visible:ring-offset-0';

  return (
    <AuthLayout
      title="Set new password"
      pageTitle="Reset Password"
      showTermsLinks={false}
    >
      <p className="text-center text-[#a4a4a4] text-sm -mt-2 mb-6 tracking-[-0.14px]">
        Enter your new password below
      </p>

      <AnimatePresence mode="wait">
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-red-500/20 border border-red-500/50 text-red-200 p-3 rounded-xl flex items-start gap-2 mb-6"
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span className="text-sm">{error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-green-500/20 border border-green-500/50 text-green-200 p-3 rounded-xl flex items-start gap-2 mb-6"
          >
            <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span className="text-sm">Password reset successful! Redirecting to login...</span>
          </motion.div>
        )}
      </AnimatePresence>

      {!success && accessToken && refreshToken && (
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {/* New Password field */}
          <div className="flex flex-col gap-2">
            <label htmlFor="password" className="text-sm text-[#a4a4a4] tracking-[-0.14px]">
              New Password
            </label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className={`${authInputClass} pr-12`}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-[#a4a4a4] hover:text-white hover:bg-transparent"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Confirm Password field */}
          <div className="flex flex-col gap-2">
            <label htmlFor="confirmPassword" className="text-sm text-[#a4a4a4] tracking-[-0.14px]">
              Confirm New Password
            </label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm your new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className={`${authInputClass} pr-12`}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-[#a4a4a4] hover:text-white hover:bg-transparent"
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div className="text-xs text-[#a4a4a4] tracking-[-0.12px]">
            Password must be at least 8 characters and contain uppercase, lowercase, and numeric characters.
          </div>

          {/* Reset password button */}
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full h-10 bg-white text-[#404040] font-medium rounded-[36px] hover:bg-white/90 text-base tracking-[-0.16px]"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Resetting password...
              </span>
            ) : (
              'Reset Password'
            )}
          </Button>

          <div className="text-center text-sm text-[#a4a4a4] tracking-[-0.14px]">
            <Link to="/login" className="text-white underline underline-offset-4 hover:text-white/80">
              Back to login
            </Link>
          </div>
        </form>
      )}

      {!accessToken && !refreshToken && !error && (
        <div className="text-center text-sm text-[#a4a4a4] tracking-[-0.14px]">
          <Link to="/login" className="text-white underline underline-offset-4 hover:text-white/80">
            Back to login
          </Link>
        </div>
      )}
    </AuthLayout>
  );
};

export default ResetPassword;
